'use client';

import { useObservable } from '@legendapp/state/react';
import { $React } from '@legendapp/state/react-web';
import { type ChangeEvent, type FocusEvent, type InputHTMLAttributes } from 'react';
import { batch } from '@legendapp/state';
import { motion } from 'framer-motion';
import { AnimatePresenceShow } from '@/components/AnimatePresenceShow';
import { Typography } from '@/components/Typography';
import { cn } from '@/lib/cn';
import { motionFadeIn } from '@/styles/motionAnimations';

export interface PromoCodeFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
    label: string;
    labelClassName?: string;
}

export default function PromoCodeField({ label, value, placeholder, onChange, onBlur, labelClassName, ...props }: PromoCodeFieldProps) {
    const promoCodeState = useObservable({
        promoCode: value || '',
        autoFocus: false,
        visible: false,
    });

    return (
        <AnimatePresenceShow
            if={() => {
                const visible = promoCodeState.visible.get();
                const promoCode = promoCodeState.promoCode.get();
                return Boolean(visible || promoCode);
            }}
            else={() => (
                <Typography
                    variant="paragraph-heavy"
                    as={motion.label}
                    key="toggle"
                    className={cn('flex gap-3 items-center whitespace-nowrap h-13 min-w-31', labelClassName)}
                    {...motionFadeIn}
                >
                    <$React.input
                        type="checkbox"
                        name="promoCode"
                        className="checkbox rounded border-base-content"
                        $checked={() => promoCodeState.visible.get()}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            batch(() => {
                                promoCodeState.autoFocus.set(true);
                                promoCodeState.visible.set(e.currentTarget.checked);
                            });
                        }}
                    />
                    {label}
                </Typography>
            )}
        >
            <motion.div key="text-input" className="h-13 min-w-31" {...motionFadeIn}>
                <$React.input
                    ref={(ref: HTMLInputElement) => {
                        if (promoCodeState.autoFocus.get()) {
                            ref?.focus();
                            promoCodeState.autoFocus.set(false);
                        }
                    }}
                    type="text"
                    placeholder={placeholder || label}
                    className="input h-13 placeholder:text-base-content"
                    $value={() => promoCodeState.promoCode.get()}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        onChange?.(e);
                        promoCodeState.promoCode.set(e.target.value?.trim());
                    }}
                    onBlur={(e: FocusEvent<HTMLInputElement>) => {
                        onBlur?.(e);
                        if (!e.target.value?.trim()) {
                            promoCodeState.visible.set(false);
                        }
                    }}
                    {...props}
                />
            </motion.div>
        </AnimatePresenceShow>
    );
}
